import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { propertyService } from "../../_services";

const errorText = (error, fallback) => typeof error === "string" && error.trim() ? error :
  typeof error?.message === "string" && error.message.trim() ? error.message : fallback;

// One lock covers every step's Save as Draft and the final publication action.
// An uncertain publication must never be followed by a Draft write (which would
// demote a successfully submitted Pending listing).
export function usePropertySubmission({ formData, propertyId, setFormData, setPropertyId, canSubmit, onSuccess }) {
  const lock = useRef(null);
  const uncertainId = useRef("");
  const checking = useRef(false);
  const [operation, setOperation] = useState("idle");
  const [message, setMessage] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);

  const begin = kind => {
    if (lock.current) {
      if (lock.current === "unresolved") toast.info("Check the submission status before saving or submitting again.");
      return false;
    }
    lock.current = kind; setOperation(kind); setMessage("");
    return true;
  };
  const accepted = () => {
    lock.current = "succeeded"; setOperation("succeeded"); setMessage(""); onSuccess();
  };
  const rejected = text => {
    lock.current = null; setOperation("idle"); setMessage(text);
    toast.error(text);
  };

  const checkStatus = async () => {
    const id = uncertainId.current;
    if (!id || lock.current !== "unresolved" || checking.current) return;
    checking.current = true; setCheckingStatus(true);
    try {
      const response = await propertyService.getDetail(id);
      const property = response?.data;
      if (response?.status === true && String(property?._id || property?.id) === String(id) && property.status === "Pending") {
        accepted();
      } else {
        // Even a Draft read cannot prove a timed-out request has stopped running.
        setMessage("Submission is not yet confirmed. Check again or review Properties before making further changes.");
      }
    } catch (error) {
      setMessage("Unable to confirm submission status. Check again or review Properties before making further changes.");
    } finally {
      checking.current = false; setCheckingStatus(false);
    }
  };

  const submit = async () => {
    if (!canSubmit() || !begin("publishing")) return false;
    const id = propertyId || "";
    try {
      const response = await propertyService.add({ ...formData, propertyId: id });
      if (response?.status === true) { accepted(); return true; }
      if (response?.status === false) {
        rejected(errorText(response, "The listing was not submitted. Please review the form and try again."));
        return false;
      }
      throw new Error("Unrecognized submission response");
    } catch (error) {
      if (error?.propertySubmissionNotSent === true) {
        rejected(errorText(error, "Unable to prepare the listing. Please try again."));
      } else {
        uncertainId.current = id;
        lock.current = "unresolved"; setOperation("unresolved");
        setMessage("We could not confirm whether the listing was submitted. Your entries are retained. Check Properties before trying again.");
        if (id) await checkStatus();
      }
      return false;
    }
  };

  const saveAsDraft = async (options = {}) => {
    if (!begin("saving")) return false;
    const progress = options?.setProgressText || (() => {});
    const payload = { ...formData, propertyId };
    try {
      progress("0%");
      const response = await propertyService.saveAsDraft(payload, value => progress(`${Math.round(value.progress)}%`));
      if (response?.status !== true || !response.data?.propertyId) {
        rejected(errorText(response, "Unable to save the draft. Your entries have been retained."));
        return false;
      }
      setPropertyId(response.data.propertyId);
      setFormData(current => {
        const saved = { ...current };
        // Do not discard photos added while an earlier save was in flight.
        if (current.photos === payload.photos) {
          delete saved.photos; saved.images = response.data.images;
        }
        return saved;
      });
      toast.success(response.message || "Draft saved.");
      options?.onComplete?.();
      return true;
    } catch (error) {
      rejected(errorText(error, "Unable to save the draft. Your entries have been retained."));
      return false;
    } finally {
      progress("");
      lock.current = null; setOperation("idle");
    }
  };

  return {
    submit, saveAsDraft, checkStatus, message, checkingStatus,
    requesting: operation === "publishing", draftRequesting: operation === "saving",
    writesBlocked: operation !== "idle", unresolved: operation === "unresolved",
    canCheckStatus: operation === "unresolved" && Boolean(uncertainId.current),
  };
}
